export enum AuthType {
  Microsoft = 'MS',
  Standard = 'TT',
  Google = 'GG',
}

export type AuthLifecycle =
  | 'unauthenticated'
  | 'bootstrapping'
  | 'registering'
  | 'sessionReady'
  | 'fatal';

export type CalendarAccess =
  | 'unknown'
  | 'pending'
  | 'ready'
  | 'consentRequired'
  | 'disabled';

type AuthErrorCode =
  | 'emailMismatch'
  | 'consentRequired'
  | 'popupClosed'
  | 'networkError'
  | 'tokenExpired'
  | 'cancelledByUser'
  | 'unknown';

export interface AuthError {
  code: AuthErrorCode;
  message: string;
  correlationId?: string;
}

export interface AuthMachineState {
  lifecycle: AuthLifecycle;
  calendar: CalendarAccess;
  lastError?: AuthError;
  lastConsentPromptAt?: number;
  lastConsentPromptScopeKey?: string;
  authType: AuthType | null;
  oid?: string;
  teamsUserId?: string;
}

export interface SessionTokens {
  idToken: string;
  accessToken: string;
  expiresOn: number;
  oid: string;
}

export interface GraphTokens {
  accessToken: string;
  scopes: string[];
  expiresOn: number;
}

interface AuthHeaders {
  Authorization?: string;
  AccessToken?: string;
  'x-auth-type'?: AuthType;
  idToken?: string;
}

export type AcquireSessionSource =
  | 'bootstrap'
  | 'login'
  | 'register'
  | 'agent-401-retry'
  | 'auto';

export interface AcquireSessionOptions {
  forceRefresh?: boolean;
  source: AcquireSessionSource;
}

type AcquireGraphSource = 'auto' | 'manual';

export interface AcquireGraphOptions {
  scopes: string[];
  source: AcquireGraphSource;
  account?: { homeAccountId: string };
  /**
   * If true, escalate to an interactive consent popup when the silent acquire
   * fails with consent_required / InteractionRequiredAuthError. Pass `true`
   * for user-driven actions (manual button clicks). The default `false` keeps
   * auto/background callers silent so they can never spawn a popup.
   */
  interactive?: boolean;
}

export type BroadcastChannelMessage =
  | { type: 'sso-acquiring'; oid: string; scopes: string[]; tabId: string; nonce: string }
  | { type: 'sso-claimed'; nonce: string; tabId: string }
  | { type: 'sso-result'; nonce: string; tokens: SessionTokens }
  | { type: 'sso-failed'; nonce: string; errorCode?: string }
  | { type: 'graph-acquiring'; oid: string; scopeKey: string; tabId: string; nonce: string }
  | { type: 'graph-claimed'; nonce: string; tabId: string }
  | { type: 'graph-result'; nonce: string; tokens: GraphTokens }
  | { type: 'graph-failed'; nonce: string; errorCode?: string };

/**
 * Thrown by the gateway when a silent session acquisition is attempted but MSAL
 * holds no account — e.g. on a pre-auth page like the invite flow, or after
 * sign-out. Per-request callers on the `auto` header path catch this and fall
 * back to anonymous headers rather than surfacing the raw error to the user.
 */
export class NoMsalAccountError extends Error {
  constructor(message = 'No MSAL account available for session acquisition') {
    super(message);
    this.name = 'NoMsalAccountError';
  }
}

export const SESSION_SCOPE_KEY = 'session';

export function buildScopeKey(scopes: string[]): string {
  return scopes.slice().sort().join(' ');
}

export function buildIdentityHint(opts: { oid?: string; teamsUserId?: string; homeAccountId?: string }): string {
  if (opts.oid) return `oid:${opts.oid}`;
  if (opts.teamsUserId) return `teams:${opts.teamsUserId}`;
  if (opts.homeAccountId) return `msal:${opts.homeAccountId}`;
  return 'teams:unknown';
}
